import { Button, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { DynamicIcon } from 'src/components';
import { CorrectionPopup } from './correction-popup';

export const PendingAiSuggestionHeader = ({
  promptName,
  show = false,
  onDismiss,
  onRefine,
  onAccept,
}: {
  promptName: string;
  show: boolean;
  onDismiss: () => void;
  onRefine: (correction: string) => void;
  onAccept: () => void;
}) => {
  const correctionButtonRef = useRef<HTMLButtonElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const handleToggleCorrectionPopup = () => {
    setPopupOpen((prev) => !prev);
  };

  const handleSubmitCorrection = (correction: string) => {
    setPopupOpen(false);
    onRefine(correction || '');
  };

  if (!show) return null;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        background: (theme) => theme.palette.custom.OV.sys.tertiaryContainer,
        padding: 1,
        borderRadius: '8px 8px 0 0',
        zIndex: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        color={(theme) => theme.palette.custom.OV.sys.onTertiaryContainer}
        gap={1}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <DynamicIcon name="auto_awesome" />
        <Typography
          textOverflow="ellipsis"
          overflow="hidden"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {promptName}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1}>
        <Button
          variant="text"
          size="small"
          sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant, gap: 0.5 }}
          onClick={() => {
            onDismiss();
            setPopupOpen(false);
          }}
        >
          <DynamicIcon name="clear" /> Dismiss
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={handleToggleCorrectionPopup}
          ref={correctionButtonRef}
          sx={{ color: (theme) => theme.palette.custom.OV.sys.onTertiaryContainer, gap: 0.5 }}
        >
          <DynamicIcon name="auto_fix_high" />
          Refine
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            onAccept();
            setPopupOpen(false);
          }}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.onTertiary,
            background: (theme) => theme.palette.custom.OV.sys.tertiary,
            gap: 0.5,
            '&:hover': {
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.custom.OV.sys.onTertiaryFixed
                  : theme.palette.custom.OV.sys.tertiaryFixed,
            },
          }}
        >
          <DynamicIcon name="check" />
          Accept
        </Button>
      </Stack>

      <CorrectionPopup
        anchorEl={correctionButtonRef.current}
        open={popupOpen}
        onSubmit={handleSubmitCorrection}
        onClose={() => setPopupOpen(false)}
      />
    </Stack>
  );
};
